import type { CharacterState, Rules } from "../data/types";
import type { calcDerivedStats } from "./characterCalc";

export interface ValidationIssue {
  severity: "error" | "warning";
  message: string;
}

export function getValidationIssues(
  state: CharacterState,
  stats: ReturnType<typeof calcDerivedStats>,
  rules: Rules
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // --- Core skill points ---
  const skillDiff = stats.totalSkillPoints - stats.usedSkillPoints;
  if (skillDiff > 0) {
    issues.push({
      severity: "warning",
      message: `${skillDiff} core skill point${skillDiff !== 1 ? "s" : ""} unspent`,
    });
  } else if (skillDiff < 0) {
    issues.push({
      severity: "error",
      message: `${Math.abs(skillDiff)} core skill point${Math.abs(skillDiff) !== 1 ? "s" : ""} over budget`,
    });
  }

  // --- Incomplete pacts ---
  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];
  const incompletePacts = allPacts.filter(
    (p) => !p.darkOfferingId || !p.patronId || !p.giftId
  );
  if (incompletePacts.length > 0) {
    issues.push({
      severity: "warning",
      message: `${incompletePacts.length} pact${incompletePacts.length !== 1 ? "s are" : " is"} incomplete (missing offering, patron, or gift)`,
    });
  }

  // --- Gift of Earthly Experience without a chosen skill ---
  const missingGiftSkill = allPacts.filter(
    (p) => p.giftId === "gift-of-earthly-experience" && !p.giftSkillId
  );
  if (missingGiftSkill.length > 0) {
    issues.push({
      severity: "warning",
      message: `Gift of Earthly Experience requires a core skill selection`,
    });
  }

  // --- Gift of Choice without a completed swap ---
  const incompleteChoicePacts = allPacts.filter(
    (p) =>
      p.giftId === "gift-of-choice" &&
      (!p.choiceSwapFromId || !p.choiceSwapToId)
  );
  if (incompleteChoicePacts.length > 0) {
    issues.push({
      severity: "warning",
      message: `Gift of Choice requires selecting which offering to swap (${incompleteChoicePacts.length} pact${incompleteChoicePacts.length !== 1 ? "s" : ""} incomplete)`,
    });
  }

  // --- Spell points (per category) ---
  // Dedicated pools (destruction/warMagic/protection/sorcery) can only fund their own list.
  // The "any" pool absorbs overflow when a list's spending exceeds its dedicated pool.
  const budget = stats.totalSpellPointBudget;
  const dedicatedListIds = [
    "destruction",
    "warMagic",
    "protection",
    "sorcery",
  ] as const;
  const listLabels: Record<string, string> = {
    destruction: "Destruction",
    warMagic: "War Magic",
    protection: "Protection",
    sorcery: "Sorcery",
  };

  // Tally points spent per spell list using rules to map spell → list.
  const spentPerList: Record<string, number> = {};
  for (const [spellId, pts] of Object.entries(stats.usedSpellPoints)) {
    const listId = rules.spellLists.find((l) =>
      l.spells.some((s) => s.id === spellId)
    )?.id;
    if (!listId) continue;
    spentPerList[listId] = (spentPerList[listId] ?? 0) + pts;
  }

  // Room left in each list: points that could still be added before every
  // spell hits its cap (binary spells cap at 1, others at the spell level cap).
  // Unspent points beyond this room are unspendable and not worth warning about.
  const roomPerList: Record<string, number> = {};
  for (const list of rules.spellLists) {
    roomPerList[list.id] = list.spells
      .filter((s) => !s.deprecated)
      .reduce((sum, s) => {
        const cap =
          s.scaling === "binary" ? 1 : (stats.maxSpellLevel ?? Infinity);
        return sum + Math.max(0, cap - (stats.usedSpellPoints[s.id] ?? 0));
      }, 0);
  }

  // Check each dedicated pool for stranded (unusable) unspent points.
  for (const listId of dedicatedListIds) {
    const dedicated = budget[listId] ?? 0;
    if (dedicated === 0) continue;
    const spent = spentPerList[listId] ?? 0;
    const room = roomPerList[listId] ?? 0;
    const stranded = Math.min(dedicated - spent, room);
    if (stranded > 0) {
      roomPerList[listId] = room - stranded;
      issues.push({
        severity: "warning",
        message: `${stranded} ${listLabels[listId]} spell point${stranded !== 1 ? "s" : ""} unspent`,
      });
    }
    // Negative stranded (overspent dedicated) draws from "any" — checked below.
  }

  // Check "any" pool balance after absorbing all dedicated-pool overflow.
  const totalBudget =
    budget.any +
    budget.destruction +
    budget.warMagic +
    budget.protection +
    budget.sorcery;
  if (totalBudget > 0) {
    const totalOverflow = dedicatedListIds.reduce((sum, listId) => {
      const dedicated = budget[listId] ?? 0;
      const spent = spentPerList[listId] ?? 0;
      return sum + Math.max(0, spent - dedicated);
    }, 0);
    const anyRemaining = budget.any - totalOverflow;
    // Any-list points can only go where room remains after dedicated pools.
    const anySpendable = Math.min(
      anyRemaining,
      Object.values(roomPerList).reduce((sum, r) => sum + r, 0)
    );
    if (anySpendable > 0) {
      issues.push({
        severity: "warning",
        message: `${anySpendable} Any List spell point${anySpendable !== 1 ? "s" : ""} unspent`,
      });
    } else if (anyRemaining < 0) {
      const over = Math.abs(anyRemaining);
      issues.push({
        severity: "error",
        message: `${over} spell point${over !== 1 ? "s" : ""} over budget (exceeded Any List pool)`,
      });
    }
  }

  // --- Deprecated spells ---
  for (const alloc of state.spellAllocations) {
    if (alloc.points <= 0) continue;
    const spell = rules.spellLists
      .flatMap((l) => l.spells)
      .find((s) => s.id === alloc.spellId);
    if (spell?.deprecated) {
      issues.push({
        severity: "error",
        message: `"${spell.name}" has been removed from the game — go to Spell Allocation, clear the points, and choose a replacement`,
      });
    }
  }

  // --- Spell level cap ---
  if (stats.maxSpellLevel !== null) {
    for (const alloc of state.spellAllocations) {
      if (alloc.points <= stats.maxSpellLevel) continue;
      const spell = rules.spellLists
        .flatMap((l) => l.spells)
        .find((s) => s.id === alloc.spellId);
      issues.push({
        severity: "error",
        message: `"${spell?.name ?? alloc.spellId}" is level ${alloc.points} — spells max out at level ${stats.maxSpellLevel}`,
      });
    }
  }

  // --- Blood sacrifice HP ---
  if (stats.maxHp < 1) {
    issues.push({
      severity: "error",
      message: `Max HP is ${stats.maxHp} — too many blood sacrifices have been performed.`,
    });
  }

  return issues;
}
